import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { EmptyState, Loading, Notice, StatusPill, Tabs } from '../components/ui';
import { BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';
import { FEATURE, SMITH_OUTCOME, flagEmoji, formatDateTime, formatMs, formatTokens, formatUsd, smithRunLabel } from '../lib/format';
import type { SmithOutcome, SmithOverview, SmithRun } from '../types';
import CompanyLogo from '../components/CompanyLogo';

type LedgerFilter = 'rating' | 'all' | 'verification' | 'copilot' | 'examples';

export default function Smith() {
  const { data, loading, error, reload } = useApi<SmithOverview>('/support/smith/overview', 20000);
  const [filter, setFilter] = useState<LedgerFilter>('rating');
  const traces = data?.traces || [];
  const counts = useMemo(
    () => ({
      rating: traces.filter((t) => t.name === 'credit-rating' || t.feature === 'RATING').length,
      all: traces.length,
      verification: traces.filter((t) => t.feature === 'DOCUMENT_VERIFICATION').length,
      copilot: traces.filter((t) => t.feature === 'ANALYST_COPILOT').length,
      examples: traces.filter((t) => t.exampleKey).length,
    }),
    [traces],
  );

  if (loading && !data) return <Loading label="Loading production traces…" />;
  if (error && !data) return <Notice tone="rose">{error}</Notice>;
  if (!data) return null;

  const { examples, totals } = data;
  const visible = traces.filter((t) => {
    if (filter === 'rating') return t.name === 'credit-rating' || t.feature === 'RATING';
    if (filter === 'verification') return t.feature === 'DOCUMENT_VERIFICATION';
    if (filter === 'copilot') return t.feature === 'ANALYST_COPILOT';
    if (filter === 'examples') return Boolean(t.exampleKey);
    return true;
  });

  return (
    <div className="smith-page" data-testid="smith-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-[#f3d0df] pb-6">
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#d6006e]">Smith</p>
          <h1 className="mt-1 font-serif text-4xl font-semibold tracking-tight text-[#2b0018]">Production AI, as it ran</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#7a1f4a]">
            Every verification, group agent and lead-analyst call is captured from the local database — the same Postgres
            rows the rating pipeline writes. LangSmith-shaped traces, without leaving Micus Support.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-right">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Source</p>
            <p className="text-xs font-semibold text-[#6b0030]">{data.source}</p>
            <p className="text-[11px] text-[#c084a0]">{data.langsmithExport ? 'Also exporting to LangSmith cloud' : 'Local capture only'}</p>
          </div>
          <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} onClick={() => void reload()}>
            Refresh
          </button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-4">
        <Metric label="Traces" value={String(totals.traces)} hint="Root pipeline runs" />
        <Metric label="Tokens" value={formatTokens(totals.tokens)} hint="Prompt + completion" />
        <Metric label="AI cost" value={formatUsd(totals.costUsd)} hint="Estimated USD" />
        <Metric
          label="Unable to fix"
          value={String(totals.outcomes.UNABLE_TO_FIX)}
          hint={`${totals.outcomes.GOOD} good · ${totals.outcomes.BAD} bad`}
          danger={totals.outcomes.UNABLE_TO_FIX > 0}
        />
      </section>

      <section>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-[#9d0050]">Worked examples</p>
        <div className="grid gap-4 lg:grid-cols-2">
          <CaseDossier
            testId="smith-example-good"
            kicker="Accepted credit"
            title="A good rating"
            run={examples.good}
            empty="No good rating in the local database yet."
          />
          <CaseDossier
            testId="smith-example-unable"
            kicker="Locked findings"
            title="Unable to fix"
            run={examples.unableToFix}
            empty="No unfixable bad rating in the local database yet."
            locked
          />
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#9d0050]">Trace ledger</p>
            <p className="mt-1 text-sm text-[#b0708f]">Newest production runs first. Open a row to inspect inputs, outputs and nested spans.</p>
          </div>
          <p className="text-[11px] text-[#c084a0]">Updated {formatDateTime(data.generatedAt)}</p>
        </div>
        <div className="mb-4">
          <Tabs<LedgerFilter>
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'rating', label: 'Credit ratings', count: counts.rating },
              { id: 'verification', label: 'Verification', count: counts.verification },
              { id: 'copilot', label: 'Copilot', count: counts.copilot },
              { id: 'examples', label: 'Examples', count: counts.examples },
              { id: 'all', label: 'All traces', count: counts.all },
            ]}
          />
        </div>
        {visible.length === 0 ? (
          <EmptyState title="No traces in this view" body="Run a rating, verify a document, or switch the filter. Smith reads the local database." />
        ) : (
          <ol className="smith-ledger divide-y divide-[#f6d7e4] border-y border-[#f3d0df]" data-testid="smith-trace-list">
            {visible.map((t) => (
              <li key={t.id}>
                <TraceRow run={t} />
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value, hint, danger }: { label: string; value: string; hint: string; danger?: boolean }) {
  return (
    <div className="border-y border-[#f3d0df] py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className={`mt-1 text-2xl font-black tabular-nums ${danger ? 'text-[#be123c]' : 'text-[#2b0018]'}`}>{value}</p>
      <p className="mt-0.5 text-[11px] text-[#b0708f]">{hint}</p>
    </div>
  );
}

function CaseDossier({
  testId,
  kicker,
  title,
  run,
  empty,
  locked,
}: {
  testId: string;
  kicker: string;
  title: string;
  run: SmithRun | null;
  empty: string;
  locked?: boolean;
}) {
  if (!run) {
    return (
      <article className={`smith-dossier ${locked ? 'smith-dossier-locked' : ''}`} data-testid={testId}>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#c084a0]">{kicker}</p>
        <h2 className="mt-2 font-serif text-2xl text-[#6b0030]">{title}</h2>
        <p className="mt-3 text-sm text-[#b0708f]">{empty}</p>
      </article>
    );
  }
  const outcome = SMITH_OUTCOME[run.outcome] || SMITH_OUTCOME.NEUTRAL;
  return (
    <Link to={`/smith/${run.id}`} className={`smith-dossier block ${locked ? 'smith-dossier-locked' : ''}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#d6006e]">{kicker}</p>
          <h2 className="mt-2 font-serif text-2xl text-[#2b0018]">{title}</h2>
        </div>
        <div className="text-right">
          <p className="font-serif text-4xl font-semibold leading-none text-[#2b0018]">{run.grade || '—'}</p>
          <p className="mt-1 text-xs font-bold tabular-nums text-[#9d0050]">{run.score != null ? `${run.score}/100` : ''}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        {run.company && (
          <CompanyLogo companyId={run.company.id} name={run.company.name} hasLogo={run.company.hasLogo} className="h-10 w-10" />
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-[#6b0030]">
            {displayCompany(run)}
          </p>
          <p className="mt-0.5 line-clamp-3 text-xs leading-relaxed text-[#b0708f]">{summaryLine(run)}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <StatusPill label={outcome.label} tone={outcome.tone} />
        {locked && <StatusPill label="Cannot remediate" tone="rose" />}
        <span className="text-[11px] text-[#c084a0]">
          {formatMs(run.latencyMs)} · {formatUsd(run.rollupCostUsd ?? run.costUsd)}
        </span>
      </div>
    </Link>
  );
}

function TraceRow({ run }: { run: SmithRun }) {
  const outcome = SMITH_OUTCOME[run.outcome] || SMITH_OUTCOME.NEUTRAL;
  return (
    <Link to={`/smith/${run.id}`} className="smith-row flex items-stretch gap-4 py-3.5" data-testid={`smith-row-${run.exampleKey || run.id}`}>
      <span className="smith-row-rule" data-outcome={run.outcome} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold text-[#2b0018]">{smithRunLabel(run.name)}</p>
          <StatusPill label={outcome.label} tone={outcome.tone} />
          {run.feature && <span className="text-[11px] text-[#c084a0]">{FEATURE[run.feature] || run.feature}</span>}
        </div>
        <p className="mt-1 truncate text-xs text-[#7a1f4a]">
          {displayCompany(run)}
          {run.grade ? ` · ${run.grade} ${run.score}/100` : ''}
          {run.error ? ` · ${run.error}` : ''}
        </p>
      </div>
      <div className="hidden shrink-0 text-right sm:block">
        <p className="text-xs font-semibold tabular-nums text-[#6b0030]">{formatMs(run.latencyMs)}</p>
        <p className="text-[11px] text-[#c084a0]">
          {run.childCount ? `${run.childCount} spans · ` : ''}
          {formatTokens(run.rollupTokens ?? run.totalTokens)} · {formatUsd(run.rollupCostUsd ?? run.costUsd)}
        </p>
        <p className="text-[11px] text-[#c084a0]">{formatDateTime(run.startTime)}</p>
      </div>
    </Link>
  );
}

function summaryLine(run: SmithRun): string {
  const outputs = run.outputs as { summary?: string } | null;
  if (outputs?.summary) return outputs.summary;
  if (run.error) return run.error;
  return SMITH_OUTCOME[run.outcome as SmithOutcome]?.hint || smithRunLabel(run.name);
}

function displayCompany(run: SmithRun): string {
  if (run.company) return `${flagEmoji(run.company.countryCode)} ${run.company.name}`;
  const inputs = run.inputs as { company?: string } | null;
  if (inputs?.company) return inputs.company;
  return smithRunLabel(run.name);
}
